import { Modal, Spin, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../../styles/Components/bot/bot-reports/PerformanceReportModal.module.css";
import EmptyBox from "../../../ui/EmptyBox";
import {
  PiArrowClockwiseBold,
  PiArrowDownBold,
  PiArrowUpRightBold,
  PiCalculatorBold,
  PiCalendarBold,
  PiChartPieSliceBold,
  PiCheckCircleBold,
  PiCheckSquareOffsetBold,
  PiCoinsBold,
  PiHandCoinsBold,
  PiMoneyBold,
  PiRobotBold,
  PiScalesBold,
  PiSwapBold,
  PiTargetBold,
  PiTrendUpBold,
  PiWarningCircleBold,
} from "react-icons/pi";
import capitalize from "antd/es/_util/capitalize";
import {
  dateString,
  formatCustomDate,
  formatPrice,
} from "../../../utility/helper";
import { Link } from "react-router-dom";
import { HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { riskAppetiteColor, riskOptions } from "../../../utility/data";
import useUserData from "../../../hooks/useUserData";
import { getBotPerformanceReportPdf } from "../../../redux/bot/actions";

function parseTradeReport(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object") return [];
    return Object.entries(parsed).map(([currency, details]) => ({
      currency,
      ...(details || {}),
    }));
  } catch {
    return [];
  }
}

function PerformanceReportModal({
  open,
  close,
  bot,
  botInfo,
  isMiniApp = false,
}) {
  const {
    botState: {
      performanceReports,
      performanceReportsStatus,
      performanceReportsError,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const { email, username } = useUserData();

  const status = performanceReportsStatus?.[bot];
  const report = performanceReports?.[bot];

  const {
    pdf_url: pdf,
    ai_assistant_mode: strategy,
    exchange_id: exchange,
    plot_image_base64: plot,
    overall_win_rate: winRate,
    risk_apetite: risk,
    start_date: start,
    end_date: end,
    ai_assistant_mode: aiMode,
    total_closed_positions: closedPositions,
    overall_accuracy: accuracy,
    total_profitable_positions: profitablePositions,
    overall_profit_percent: profitPercent,
    total_profits_usd: profitUsd,
    num_coins_traded: numCoins,
    cagr,
    max_drawdown: maxDrawdown,
    risk_reward_ratio: riskReward,
    profit_factor: profitFactor,
    expectancy,
    trade_report: tradeReportRaw,
  } = report || {};

  const tradeCoins = isMiniApp ? parseTradeReport(tradeReportRaw) : [];

  const {
    bot_name: name,
    status: botStatus,
    start_date: startDate,
    end_date: endDate,
  } = botInfo;

  const getReport = () => {
    dispatch(getBotPerformanceReportPdf(username, email, bot));
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      centered
      footer={null}
      title={`${name} - Detailed Performance`}
      styles={{
        body: {
          minHeight: "150px",
          // display: "flex",
          width: "100%",
        },
        header: { marginBottom: "20px" },
      }}
      width={isMiniApp ? "min(680px, calc(100vw - 24px))" : 680}
      className={styles.modal}
    >
      {status === "loading" && (
        <div className={styles.loader}>
          <Spin />
          <h4>Generating performance report...</h4>
        </div>
      )}
      {status === "success" ? (
        pdf ? (
          <div className={styles.info}>
            <ul className={styles.infoList}>
              {/* === SUMMARY HEADER === */}
              <li className={styles.sectionHeader}>Bot Overview</li>

              <li>
                <span className={styles.label}>
                  <PiRobotBold /> Bot Name:
                </span>
                <span className={styles.value}>{name}</span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiCheckCircleBold /> Status:
                </span>
                <span
                  className={styles.value}
                  style={botStatus === "Active" ? { color: "#0AAF47" } : {}}
                >
                  {botStatus}
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiChartPieSliceBold /> Risk Appetite:
                </span>
                <span
                  className={styles.value}
                  style={{ color: riskAppetiteColor?.[+risk - 1] }}
                >
                  {riskOptions?.[+risk - 1]}
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiSwapBold /> Exchange:
                </span>
                <span className={styles.value}>
                  {exchange === "cryptocrispy"
                    ? "Demo Account"
                    : capitalize(exchange)}
                </span>
              </li>

              {/* === PERFORMANCE METRICS === */}
              <li className={styles.sectionHeader}>Performance</li>

              <li>
                <span className={styles.label}>
                  <PiTrendUpBold /> Win Rate:
                </span>
                <span
                  className={styles.value}
                  style={winRate >= 50 ? { color: "#0AAF47" } : {}}
                >
                  {winRate == null || winRate === ""
                    ? "-"
                    : String(winRate).includes("%")
                      ? winRate
                      : `${winRate}%`}
                </span>
                {/*<span className={styles.value}>*/}
                {/*  (Risk/Reward: {riskReward})*/}
                {/*</span>*/}
              </li>

              <li>
                <span className={styles.label}>
                  <PiTargetBold /> Signal Accuracy:
                </span>
                <span className={styles.value}>{accuracy}%</span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiHandCoinsBold /> Total Return:
                </span>
                <span
                  className={styles.value}
                  style={
                    profitPercent >= 0
                      ? { color: "#0AAF47" }
                      : { color: "#FF4D4F" }
                  }
                >
                  {profitPercent}%
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiMoneyBold /> Total Earnings:
                </span>
                <span
                  className={styles.value}
                  style={
                    profitUsd >= 0 ? { color: "#0AAF47" } : { color: "#FF4D4F" }
                  }
                >
                  {profitUsd} USD
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiArrowUpRightBold /> Annual Growth Rate (CAGR):
                </span>
                <span className={styles.value}>{cagr}%</span>
              </li>

              {/* === TRADE ACTIVITY === */}
              <li className={styles.sectionHeader}>Trade Activity</li>

              <li>
                <span className={styles.label}>
                  <PiCheckSquareOffsetBold /> Winning Positions:
                </span>
                <span className={styles.value}>{profitablePositions}</span>
                <span className={styles.value}>
                  (Closed: {closedPositions})
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiCoinsBold /> Assets Traded:
                </span>
                <span className={styles.value}>{numCoins}</span>
              </li>

              {/* === RISK & EFFICIENCY === */}
              <li className={styles.sectionHeader}>Risk & Efficiency</li>

              <li>
                <span className={styles.label}>
                  <PiArrowDownBold /> Max Drawdown:
                </span>
                <span
                  className={styles.value}
                  style={{ color: maxDrawdown > 20 ? "#FF4D4F" : "#0AAF47" }}
                >
                  {maxDrawdown}%
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiScalesBold /> Gross Profit / Loss Ratio:
                </span>
                <span
                  className={styles.value}
                  style={profitFactor >= 1.5 ? { color: "#0AAF47" } : {}}
                >
                  {profitFactor}
                </span>
              </li>

              <li>
                <span className={styles.label}>
                  <PiCalculatorBold /> Expected Value per Trade:
                </span>
                <span
                  className={styles.value}
                  style={
                    expectancy >= 0
                      ? { color: "#0AAF47" }
                      : { color: "#FF4D4F" }
                  }
                >
                  {expectancy}
                </span>
              </li>

              {/* === TEST DETAILS === */}
              <li className={styles.sectionHeader}>Performance Details</li>

              <li>
                <span className={styles.label}>
                  <PiCalendarBold /> Performance Period:
                </span>
                <span className={styles.value}>
                  {dateString(start)} → {dateString(end)}
                </span>
              </li>
            </ul>

            <div className={styles.chart}>
              <div className={styles.chartHeader}>
                <h4>Balance Performance</h4>
                {!isMiniApp && (
                  <Link
                    to={`/bot-automation/performance-report?pdf=${pdf}`}
                    style={{ fontSize: "12px" }}
                    className={styles.pdfLink}
                  >
                    <HiOutlineClipboardDocumentList size={18} />
                    Detailed Report
                  </Link>
                )}
              </div>
            </div>
            <div className={styles.chartBox}>
              <img
                src={`data:image/png;base64,${plot}`}
                alt={"plot image"}
                className={styles.plotImg}
              />
            </div>
            {isMiniApp && (
              <ul className={`${styles.infoList} ${styles.tradeList}`}>
                <li className={styles.sectionHeader}>Trades</li>
                {tradeCoins.length === 0 ? (
                  <li>This bot has no trades yet.</li>
                ) : (
                  tradeCoins.map((coin) => (
                    <li key={coin.currency} className={styles.coinBlock}>
                      <span className={styles.label}>{coin.currency}</span>
                      <span className={styles.value}>
                        Win rate {coin.win_rate ?? "-"}%
                      </span>
                      <span className={styles.value}>
                        Closed {coin.closed_positions ?? "-"}
                      </span>
                      <span className={styles.value}>
                        Profitable {coin.profitable_positions ?? "-"}
                      </span>
                      {(coin.trade_history || []).map((order, index) => (
                        <span
                          key={`${coin.currency}-${order.created}-${index}`}
                          className={styles.tradeRow}
                        >
                          <span>
                            {order.created
                              ? formatCustomDate(order.created, "show")
                              : "-"}
                          </span>
                          <span>{order.order_type || "-"}</span>
                          <span>{order.size ?? "-"}</span>
                          <span>{formatPrice(order.price)}</span>
                          <span>{order.status || "-"}</span>
                        </span>
                      ))}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        ) : (
          <div className={styles.emptyReport}>
            <EmptyBox useCase="table" size={40} />
            <div className={styles.emptyReportContent}>
              <h3>No Report Available Yet</h3>
              <p>
                The performance report for this bot is not ready yet. Reports
                may take a few moments to generate — please try again shortly.
              </p>
            </div>
            <button
              type="button"
              className={styles.retryButton}
              onClick={getReport}
            >
              <PiArrowClockwiseBold size={16} />
              Retry Report
            </button>
          </div>
        )
      ) : (
        ""
      )}

      {(status === "error" ||
        (performanceReportsError && status !== "loading" && status !== "success")) && (
        <div className={styles.emptyReport}>
          <div className={styles.errorReportIcon}>
            <PiWarningCircleBold size={30} />
          </div>
          <div className={styles.errorReportContent}>
            <h3>Unable to Load Report</h3>
            <p>
              We ran into a problem while loading your performance report.
              This is usually temporary — please try again in a few moments.
            </p>
          </div>
          <button
            type="button"
            className={styles.retryButton}
            onClick={getReport}
          >
            <PiArrowClockwiseBold size={16} />
            Retry Report
          </button>
        </div>
      )}
    </Modal>
  );
}

export default PerformanceReportModal;
